import {store} from './store'
import Api from '@/providers/Api'
import i18n from '@/providers/i18n'
import router from './router'
import * as _ from 'lodash'

/**
 * Service to manage user authentication/authorization state against Yii API.
 */

export default {

    // Check if the current user auth_key is valid - if it is not, as a side effect the user will be logged out.
    // This called when the app is loaded.
    checkAuth() {
        const path = router.currentRoute.path
        const name = router.currentRoute.name
        const skipCheckAuth = !!router.currentRoute.meta && router.currentRoute.meta.skipCheckAuth
        if (path != '/user/login' && !skipCheckAuth && name != 'resetConfirm') {
            // interceptor will logout if auth is not valid (see Api.checkAuth)
            return Api.checkAuth()
        }
    },

    async loginUser(vue, email, password) {
        try {
            const response = await Api.loginUser(vue, email, password)
            if (response.body.auth_key) {
                store.state.currentUser = response.body
                store.state.yii2MaskingUserStack = []

                this.persistUserState()
                Api.log(vue, 'user', null, 'login',
                    `User logged in: ${email}`)
                router.push('/')
            } else {
                store.state.currentUser = {}
            }
        } catch (e) {
            alert('Login error')
        }
    },

    async logoutUser(vue) {
        try {
            await Api.logoutUser(vue)
            // NB: must log before currentUser is unset; don't log logouts generated by checkauth
            if (store.state.currentUser.email) {
                Api.log(vue, 'user', null, 'logout',
                    `User logged out: ${store.state.currentUser.email}`)
            }

            store.state.currentUser = {}
            store.state.yii2MaskingUserStack = []
            this.persistUserState()
            router.push('/')
        } catch (e) {
            alert('Logout error')
        }
    },

    async maskAsUser(userId) {
        try {
            const response = await Api.maskAsUser(userId)
            if (response.body.auth_key) {
                store.state.yii2MaskingUserStack.push(store.state.currentUser) // remember who we were
                store.state.currentUser = response.body // who we are masking as

                // if we're a practitioner masking as ourself, make ourself a participant
                if (this.isPractitionerMaskingAsSelf()) {
                    store.state.currentUser.role = 'PARTICIPANT'
                }

                this.persistUserState()
                router.push('/')
            }
        } catch (e) {
            alert('Masking error')
        }
    },

    async unmaskUser() {
        try {
            const maskingAsSelf = this.isPractitionerMaskingAsSelf()
            const previousUserId = store.state.currentUser.participantId
            const previousAuthKey = store.state.currentUser.auth_key

            // NB: no need for a server-side unmask action in Yii 2, as we just pop the user here...
            store.state.currentUser = store.state.yii2MaskingUserStack.pop()

            if (maskingAsSelf) {
                // grab auth_key from previous masking user -
                // the other option below won't work in this case, because the practitioner's auth_key will
                // always have been replaced on the previous masking, and the mask request will be unauthorized
                store.state.currentUser.auth_key = previousAuthKey
                this.persistUserState()
                router.push('/')
            } else {
                // "mask" as the original user, just to refresh their auth_key
                // (it may have expired during the masking session)
                const response = await Api.maskAsUser(store.state.currentUser.id)

                // NB: leave store.state.yii2MaskingUserStack as is, as we aren't doing a real mask
                store.state.currentUser = response.body
                this.persistUserState()

                if (this.practitionerId()) {
                    // if we were a masking doc, go back to patient chart
                    router.push(`/participant/chart/id/${previousUserId}`)
                } else if (this.roleIsCompanyAdmin() || this.roleIsResellerAdmin()) {
                    router.push('/')
                } else {
                    router.push('/participant/admin')
                }
            }
        } catch (e) {
            alert('Unmasking error')
        }
    },

    // Switches the current practice for a currently logged-in practitioner.
    // This is implemented as a login-like action in order to retain the relative simplicity of member functions
    // such as practitionerPracticeId(), so we do not need to complicate the currentUser state.
    // Note that the switch-practice functionality is independent of the masking stack.
    async switchToPractice(vue, practice) {
        try {
            const response = await Api.switchToPractice(vue, practice.id)

            if (response.body.auth_key) {
                store.state.currentUser = response.body
                this.persistUserState()
                Api.log(vue, 'user', null, 'switchToPractice',
                    `User switched to practice: ${practice.name}`)
                router.push('/')
            }
        } catch (e) {
            alert('Error switching practice')
        }
    },

    async switchToCompany(vue, company) {
        try {
            const response = await Api.switchToCompany(vue, company.id)

            if (response.body.auth_key) {
                store.state.currentUser = response.body
                this.persistUserState()
                Api.log(vue, 'user', null, 'switchToCompany',
                    `User switched to company: ${company.name}`)
                router.push('/')
                // router.push('/') alone does not properly update company, router.go does a complete page refresh
                router.go(0)
            }
        } catch (e) {
            alert('Error switching company')
        }
    },

    resetPassword(vue, email, password) {
        return Api.resetPassword(vue, email, password)
    },
    confirmPasswordReset(vue, email, confirm) {
        return Api.confirmPasswordReset(vue, email, confirm)
    },
    loadEnvironment() {
        return Api.loadEnvironment()
            .then((response) => {
                store.state.environment = response.body
            })
    },

    // load the current user state from local storage
    loadUserState() {
        let stack = JSON.parse(localStorage.getItem('yii2MaskingUserStack'))
        if (!_.isArray(stack)) {
            stack = []
        }
        store.state.yii2MaskingUserStack = stack
        store.state.currentUser = JSON.parse(localStorage.getItem('currentUser')) || {}
        store.state.language = JSON.parse(localStorage.getItem('language')) || 'en'
    },

    // save the current user state to local storage
    persistUserState() {
        localStorage.setItem('yii2MaskingUserStack', JSON.stringify(store.state.yii2MaskingUserStack))
        localStorage.setItem('currentUser', JSON.stringify(store.state.currentUser))
        localStorage.setItem('language', JSON.stringify(store.state.language))
    },

    noLoggedInUser() {
        return store.state.currentUser.role == undefined
    },
    userId() {
        return store.state.currentUser.id
    },
    participantId() {
        return store.state.currentUser.participantId
    },
    practitionerId() {
        if (this.isMaskingPractitioner()) {
            return _.last(store.state.yii2MaskingUserStack).practitionerId
        } else {
            return store.state.currentUser.practitionerId
        }
    },
    practitionerPracticeId() {
        if (this.isMaskingPractitioner()) {
            return _.last(store.state.yii2MaskingUserStack).practitionerPracticeId
        } else {
            return store.state.currentUser.practitionerPracticeId
        }
    },
    // may contain multiple types (e.g. 'PRIMARY,MENTOR')
    practitionerPracticeType() {
        if (this.isMaskingPractitioner()) {
            return _.last(store.state.yii2MaskingUserStack).practitionerPracticeType
        } else {
            return store.state.currentUser.practitionerPracticeType
        }
    },

    // lists the practitioner records for the current user (typically only one, for a practitioner)
    practitioners() {
        return store.state.currentUser.practitioners
    },
    // lists the practices that the current user belongs to (typically only one, for a practitioner)
    practices() {
        return _.map(this.practitioners(), (p) => {
            return p.practice
        })
    },
    // the practice that is active for the currently logged-in practitioner
    currentPractice() {
        return _.find(this.practices(), (practice) => {
            return practice.id == this.practitionerPracticeId()
        }) || {}
    },
    // non-active practices for the currently logged-in practitioner (typically none)
    otherPractices() {
        return _.filter(this.practices(), (practice) => {
            return practice.id != this.practitionerPracticeId() && !practice.disabled
        })
    },
    // get companies for multi-company admins
    companies() {
        return store.state.currentUser.companies
    },
    otherCompanies() {
        const filtered = _.filter(this.companies(), (company) => {
            return company.id != store.state.currentUser.company.id
        })
        return filtered
    },

    isMasking() {
        return store.state.yii2MaskingUserStack.length > 0
    },
    isMaskingAdmin() {
        return this.isMasking() && _.last(store.state.yii2MaskingUserStack).role == 'ADMIN'
    },
    isMaskingPractitioner() {
        return this.isMasking() && _.last(store.state.yii2MaskingUserStack).practitionerRole != null
    },
    isPractitionerMaskingAsSelf() {
        return this.isMasking() && _.last(store.state.yii2MaskingUserStack).id == store.state.currentUser.id
    },

    email() {
        return store.state.currentUser.email
    },
    fullname() {
        return store.state.currentUser.participant.fullname
    },
    role() {
        return store.state.currentUser.role
    },
    roleIs(role) {
        return store.state.currentUser.role == role
    },
    roleIsAdmin() {
        return this.roleIs(this.USER_ROLES.ADMIN)
    },
    roleIsCompanyAdmin() {
        return this.roleIs(this.USER_ROLES.COMPANY_ADMIN) || this.roleIs(this.USER_ROLES.MULTI_COMPANY_ADMIN)
    },
    roleIsResellerAdmin() {
        return this.roleIs(this.USER_ROLES.RESELLER_ADMIN)
    },
    adminCompany() {
        return store.state.currentUser.company
    },
    reseller() {
        return store.state.currentUser.reseller
    },
    practitionerRole() {
        return store.state.currentUser.practitionerRole
    },
    practitionerRoleIs(role) {
        return store.state.currentUser.practitionerRole == role
    },
    practitionerPracticeLacksPracticeManager() {
        return store.state.currentUser.practitionerPracticeLacksPracticeManager
    },

    USER_ROLES: {
        ADMIN: 'ADMIN',
        RESELLER_ADMIN: 'RESELLER_ADMIN',
        COMPANY_ADMIN: 'COMPANY_ADMIN',
        MULTI_COMPANY_ADMIN: 'MULTI_COMPANY_ADMIN',
        PRACTITIONER: 'PRACTITIONER',
        PARTICIPANT: 'PARTICIPANT',
    },

    PRACTITIONER_ROLES: {
        PHYSICIAN: 'PHYSICIAN',
        MEDICAL_SPECIALIST: 'FIELD_PRACTITIONER_MEDICAL_SPECIALIST',
        NURSE_PRACTITIONER: 'NURSE_PRACTITIONER',
        PHYSICIAN_ASSISTANT: 'PHYSICIAN_ASSISTANT',
        NURSE_SPECIALIST: 'NURSE_SPECIALIST',
        NURSE: 'NURSE',
        MEDICAL_ASSISTANT: 'MEDICAL_ASSISTANT',
        PRACTICE_MANAGER: 'PRACTICE_MANAGER',
        WELLNESS_COACH: 'WELLNESS_COACH',
        HEALTH_MENTOR: 'HEALTH_MENTOR',
        DIETITIAN: 'DIETITIAN',
        EXERCISE_SPECIALIST: 'EXERCISE_SPECIALIST',
    },

    switchLanguageLabel() {
        return (store.state.language == 'en' ? 'Español' : 'English')
    },
    switchLanguage() {
        i18n.switchLanguage(store)
        localStorage.setItem('language', JSON.stringify(store.state.language))
    },
}
